import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/App';
import { Plus, Trash2, Save, Check, X, Home, MapPin, Zap, Flame, Car, Calendar, Wifi, Search, RefreshCw, Bell } from 'lucide-react';
import PushNotificationManager from '@/components/PushNotificationManager';

const Settings = () => {
  const [credentials, setCredentials] = useState([]);
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingType, setEditingType] = useState(null);
  const [editingHouse, setEditingHouse] = useState(null);
  const [formData, setFormData] = useState({});
  const [houseFormData, setHouseFormData] = useState({});
  const [saveStatus, setSaveStatus] = useState({});
  
  // Energy prices state
  const [energyPrices, setEnergyPrices] = useState({
    electricity_price_kwh: 0.1883,
    electricity_subscription_monthly: 22.38,
    gas_price_kwh: 0.12,
    ev_charging_price_kwh: 0.1816,
  });
  const [editingPrices, setEditingPrices] = useState(false);
  const [priceForm, setPriceForm] = useState({});
  const [priceSaveStatus, setPriceSaveStatus] = useState(null);
  
  // Tuya local control state
  const [localDevices, setLocalDevices] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [loadingLocal, setLoadingLocal] = useState(false);

  const integrationTypes = [
    {
      type: 'smartlife',
      name: 'SmartLife / Tuya',
      fields: [
        { key: 'api_key', label: 'API Key', type: 'text' },
        { key: 'api_secret', label: 'API Secret', type: 'password' },
        { key: 'region', label: 'Region', type: 'text', placeholder: 'eu, us, cn' },
      ],
    },
    {
      type: 'alexa',
      name: 'Amazon Alexa',
      fields: [
        { key: 'client_id', label: 'Client ID', type: 'text' },
        { key: 'client_secret', label: 'Client Secret', type: 'password' },
        { key: 'refresh_token', label: 'Refresh Token', type: 'password' },
      ],
    },
    {
      type: 'vicare',
      name: 'ViCare Viessmann',
      fields: [
        { key: 'username', label: 'Email', type: 'email' },
        { key: 'password', label: 'Mot de passe', type: 'password' },
        { key: 'client_id', label: 'Client ID', type: 'text' },
      ],
    },
    {
      type: 'pellet_italia',
      name: 'Stufe a Pellet Italia',
      fields: [
        { key: 'api_key', label: 'API Key', type: 'text' },
        { key: 'device_id', label: 'Device ID', type: 'text' },
      ],
    },
    {
      type: 'growatt',
      name: 'Growatt (Solaire)',
      fields: [
        { key: 'username', label: 'Username', type: 'text' },
        { key: 'password', label: 'Password', type: 'password' },
      ],
    },
    {
      type: 'nrlink',
      name: 'NRLink (Consommation)',
      fields: [
        { key: 'api_key', label: 'API Key', type: 'text' },
        { key: 'username', label: 'Username', type: 'text' },
      ],
    },
    {
      type: 'kasa',
      name: 'Kasa (TP-Link)',
      fields: [
        { key: 'username', label: 'Email', type: 'email' },
        { key: 'password', label: 'Mot de passe', type: 'password' },
      ],
    },
    {
      type: 'wemo',
      name: 'Wemo (Belkin)',
      fields: [
        { key: 'username', label: 'Email', type: 'email' },
        { key: 'password', label: 'Mot de passe', type: 'password' },
      ],
    },
    {
      type: 'ewelink',
      name: 'eWeLink',
      fields: [
        { key: 'username', label: 'Email', type: 'email' },
        { key: 'password', label: 'Mot de passe', type: 'password' },
        { key: 'region', label: 'Region', type: 'text', placeholder: 'eu, us, as' },
      ],
    },
    {
      type: 'aqara',
      name: 'Aqara Home',
      fields: [
        { key: 'app_id', label: 'App ID', type: 'text' },
        { key: 'app_key', label: 'App Key', type: 'password' },
        { key: 'key_id', label: 'Key ID', type: 'text' },
      ],
    },
  ];

  useEffect(() => {
    loadCredentials();
    loadHouses();
    loadEnergyPrices();
    loadLocalStatus();
  }, []);

  const loadEnergyPrices = async () => {
    try {
      const res = await api.get('/energy/prices');
      if (res.data?.success) {
        setEnergyPrices(res.data.prices);
      }
    } catch (error) {
      console.error('Error loading energy prices:', error);
    }
  };

  const loadLocalStatus = async () => {
    try {
      setLoadingLocal(true);
      const res = await api.get('/integrations/tuya/local/status');
      if (res.data?.success) {
        setLocalDevices(res.data);
      }
    } catch (error) {
      console.error('Error loading local status:', error);
    } finally {
      setLoadingLocal(false);
    }
  };

  const handleScanNetwork = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const res = await api.post('/integrations/tuya/local/scan');
      setScanResult(res.data);
      if (res.data?.success) {
        await loadLocalStatus();
      }
    } catch (error) {
      setScanResult({ success: false, error: error.message });
    } finally {
      setScanning(false);
    }
  };

  const handleLoadSavedIPs = async () => {
    try {
      const res = await api.post('/integrations/tuya/local/load-saved');
      if (res.data?.success) {
        await loadLocalStatus();
      }
    } catch (error) {
      console.error('Error loading saved IPs:', error);
    }
  };

  const handleEditPrices = () => {
    setEditingPrices(true);
    setPriceForm({ ...energyPrices });
  };

  const handleSavePrices = async () => {
    setPriceSaveStatus('saving');
    try {
      const res = await api.put('/energy/prices', priceForm);
      if (res.data?.success) {
        setEnergyPrices(res.data.prices);
        setPriceSaveStatus('success');
        setTimeout(() => {
          setPriceSaveStatus(null);
          setEditingPrices(false);
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving prices:', error);
      setPriceSaveStatus('error');
      setTimeout(() => setPriceSaveStatus(null), 3000);
    }
  };

  const loadCredentials = async () => {
    try {
      const response = await api.get('/credentials');
      setCredentials(response.data);
    } catch (error) {
      console.error('Error loading credentials:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHouses = async () => {
    try {
      const response = await api.get('/houses');
      setHouses(response.data);
    } catch (error) {
      console.error('Error loading houses:', error);
    }
  };

  const handleEditHouse = (house) => {
    setEditingHouse(house.id);
    setHouseFormData({ name: house.name, address: house.address || '', description: house.description || '' });
  };

  const handleSaveHouse = async (houseId) => {
    try {
      setSaveStatus({ ...saveStatus, [houseId]: 'saving' });
      await api.put(`/houses/${houseId}`, houseFormData);
      
      // Mettre à jour localStorage pour les alertes
      const updatedHouse = houses.find(h => h.id === houseId);
      if (updatedHouse && localStorage.getItem('currentHouseId') === houseId) {
        localStorage.setItem('currentHouseAddress', houseFormData.address || '');
      }
      
      setSaveStatus({ ...saveStatus, [houseId]: 'success' });
      setTimeout(() => {
        setSaveStatus({ ...saveStatus, [houseId]: null });
        setEditingHouse(null);
        setHouseFormData({});
        loadHouses();
      }, 1500);
    } catch (error) {
      console.error('Error saving house:', error);
      setSaveStatus({ ...saveStatus, [houseId]: 'error' });
    }
  };

  const handleEdit = (type) => {
    const existing = credentials.find((c) => c.integration_type === type);
    setEditingType(type);
    setFormData(existing ? {} : {}); // Don't show encrypted values
  };

  const handleSave = async (type) => {
    try {
      setSaveStatus({ ...saveStatus, [type]: 'saving' });
      
      const existing = credentials.find((c) => c.integration_type === type);
      
      if (existing) {
        await api.put(`/credentials/${type}`, {
          credentials: formData,
        });
      } else {
        await api.post('/credentials', {
          integration_type: type,
          credentials: formData,
        });
      }
      
      setSaveStatus({ ...saveStatus, [type]: 'success' });
      setTimeout(() => {
        setSaveStatus({ ...saveStatus, [type]: null });
        setEditingType(null);
        setFormData({});
        loadCredentials();
      }, 1500);
    } catch (error) {
      console.error('Error saving credentials:', error);
      setSaveStatus({ ...saveStatus, [type]: 'error' });
      setTimeout(() => setSaveStatus({ ...saveStatus, [type]: null }), 3000);
    }
  };

  const handleDelete = async (type) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer les credentials ${type} ?`)) {
      return;
    }
    
    try {
      await api.delete(`/credentials/${type}`);
      loadCredentials();
    } catch (error) {
      console.error('Error deleting credentials:', error);
    }
  };

  const isConfigured = (type) => {
    return credentials.some((c) => c.integration_type === type && c.is_active);
  };

  return (
    <Layout title="Paramètres">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Section Maisons */}
        <div className="bg-gradient-to-r from-cyan-600 to-cyan-800 rounded-xl p-6 text-white shadow-lg">
          <h2 className="text-2xl font-bold mb-2 flex items-center">
            <Home className="w-6 h-6 mr-2" />
            Maisons
          </h2>
          <p className="text-cyan-100">
            Configurez les adresses de vos maisons pour les alertes d'urgence
          </p>
        </div>

        <div className="space-y-4">
          {houses.map((house) => {
            const isEditing = editingHouse === house.id;
            const status = saveStatus[house.id];

            return (
              <div
                key={house.id}
                className="bg-gray-800 rounded-xl p-6 shadow-lg border border-cyan-700"
                data-testid={`house-card-${house.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Home className="w-6 h-6 text-cyan-400" />
                    <h3 className="text-xl font-semibold text-white">{house.name}</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!isEditing ? (
                      <button
                        onClick={() => handleEditHouse(house)}
                        className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
                      >
                        ✏️ Modifier
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleSaveHouse(house.id)}
                          disabled={status === 'saving'}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                        >
                          {status === 'saving' ? (
                            <span>Enregistrement...</span>
                          ) : status === 'success' ? (
                            <><Check className="w-4 h-4" /> <span>Enregistré !</span></>
                          ) : (
                            <><Save className="w-4 h-4" /> <span>Enregistrer</span></>
                          )}
                        </button>
                        <button
                          onClick={() => { setEditingHouse(null); setHouseFormData({}); }}
                          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {!isEditing ? (
                  <div className="space-y-2">
                    <div className="flex items-center text-gray-300">
                      <MapPin className="w-4 h-4 mr-2 text-cyan-400" />
                      <span className="font-mono">{house.address || 'Adresse non configurée'}</span>
                    </div>
                    {house.description && (
                      <p className="text-gray-400 text-sm">{house.description}</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Nom de la maison
                      </label>
                      <input
                        type="text"
                        value={houseFormData.name || ''}
                        onChange={(e) => setHouseFormData({ ...houseFormData, name: e.target.value })}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        <MapPin className="w-4 h-4 inline mr-1" />
                        Adresse complète (pour les alertes 18/urgences)
                      </label>
                      <input
                        type="text"
                        value={houseFormData.address || ''}
                        onChange={(e) => setHouseFormData({ ...houseFormData, address: e.target.value })}
                        placeholder="Ex: 9 la Farinière, Fontaine Simon"
                        className="w-full px-4 py-2 bg-gray-700 border border-cyan-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                      <p className="text-xs text-cyan-400 mt-1">
                        Cette adresse sera annoncée par HAL en cas d'alerte incendie
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Description
                      </label>
                      <input
                        type="text"
                        value={houseFormData.description || ''}
                        onChange={(e) => setHouseFormData({ ...houseFormData, description: e.target.value })}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Section Prix Énergie */}
        <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-xl p-6 text-white shadow-lg">
          <h2 className="text-2xl font-bold mb-2 flex items-center">
            <Zap className="w-6 h-6 mr-2" />
            Tarifs Énergie
          </h2>
          <p className="text-green-100">
            Configurez les prix au kWh pour le calcul automatique des coûts
          </p>
        </div>

        <div className="bg-gray-800 rounded-xl p-6 shadow-lg border border-green-700" data-testid="energy-prices-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-white">Prix de l'énergie</h3>
            {!editingPrices ? (
              <button
                onClick={handleEditPrices}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                data-testid="edit-energy-prices-btn"
              >
                Modifier
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSavePrices}
                  disabled={priceSaveStatus === 'saving'}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="save-energy-prices-btn"
                >
                  {priceSaveStatus === 'saving' ? 'Enregistrement...' : priceSaveStatus === 'success' ? (
                    <><Check className="w-4 h-4" /> Enregistré !</>
                  ) : (
                    <><Save className="w-4 h-4" /> Enregistrer</>
                  )}
                </button>
                <button
                  onClick={() => { setEditingPrices(false); setPriceForm({}); }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {!editingPrices ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-900/30 rounded-lg border border-blue-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-blue-400" />
                  <span className="text-blue-300 font-mono font-bold">Électricité</span>
                </div>
                <div className="text-2xl font-bold text-blue-400 font-mono">{energyPrices.electricity_price_kwh}€/kWh</div>
                <div className="text-sm text-gray-500 font-mono">Abo: {energyPrices.electricity_subscription_monthly}€/mois</div>
              </div>
              <div className="p-4 bg-orange-900/30 rounded-lg border border-orange-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Flame className="w-5 h-5 text-orange-400" />
                  <span className="text-orange-300 font-mono font-bold">Gaz</span>
                </div>
                <div className="text-2xl font-bold text-orange-400 font-mono">{energyPrices.gas_price_kwh}€/kWh</div>
              </div>
              <div className="p-4 bg-cyan-900/30 rounded-lg border border-cyan-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Car className="w-5 h-5 text-cyan-400" />
                  <span className="text-cyan-300 font-mono font-bold">Recharge VE</span>
                </div>
                <div className="text-2xl font-bold text-cyan-400 font-mono">{energyPrices.ev_charging_price_kwh}€/kWh</div>
              </div>
              <div className="p-4 bg-purple-900/30 rounded-lg border border-purple-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-purple-400" />
                  <span className="text-purple-300 font-mono font-bold">Abonnement</span>
                </div>
                <div className="text-2xl font-bold text-purple-400 font-mono">{energyPrices.electricity_subscription_monthly}€/mois</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-300 mb-2">
                  <Zap className="w-4 h-4 inline mr-1" /> Prix électricité (€/kWh TTC)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  value={priceForm.electricity_price_kwh || ''}
                  onChange={(e) => setPriceForm({ ...priceForm, electricity_price_kwh: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 bg-gray-700 border border-blue-600 rounded-lg text-white font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="input-electricity-price"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-purple-300 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" /> Abonnement électricité (€/mois)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={priceForm.electricity_subscription_monthly || ''}
                  onChange={(e) => setPriceForm({ ...priceForm, electricity_subscription_monthly: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 bg-gray-700 border border-purple-600 rounded-lg text-white font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="input-subscription"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-orange-300 mb-2">
                  <Flame className="w-4 h-4 inline mr-1" /> Prix gaz (€/kWh)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  value={priceForm.gas_price_kwh || ''}
                  onChange={(e) => setPriceForm({ ...priceForm, gas_price_kwh: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 bg-gray-700 border border-orange-600 rounded-lg text-white font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="input-gas-price"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-cyan-300 mb-2">
                  <Car className="w-4 h-4 inline mr-1" /> Recharge VE (€/kWh)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  value={priceForm.ev_charging_price_kwh || ''}
                  onChange={(e) => setPriceForm({ ...priceForm, ev_charging_price_kwh: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 bg-gray-700 border border-cyan-600 rounded-lg text-white font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  data-testid="input-ev-price"
                />
              </div>
            </div>
          )}
        </div>

        {/* Section Notifications Push */}
        <PushNotificationManager />

        {/* Section Tuya Contrôle Local (tinytuya) */}
        <div className="bg-gray-800 rounded-xl p-6 shadow-lg border border-green-500/30" data-testid="tuya-local-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Wifi className="w-6 h-6 text-green-400" />
              <h2 className="text-xl font-bold text-green-400 font-mono">CONTRÔLE LOCAL TUYA</h2>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleLoadSavedIPs}
                className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm font-mono flex items-center gap-1"
                data-testid="load-saved-ips-btn"
              >
                <RefreshCw className="w-4 h-4" /> Charger IPs
              </button>
              <button
                onClick={handleScanNetwork}
                disabled={scanning}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2 font-mono"
                data-testid="scan-network-btn"
              >
                {scanning ? (
                  <><Search className="w-4 h-4 animate-spin" /> Scan en cours...</>
                ) : (
                  <><Search className="w-4 h-4" /> Scanner le réseau</>
                )}
              </button>
            </div>
          </div>

          <p className="text-sm text-gray-400 mb-4 font-mono">
            Le contrôle local (tinytuya) permet de commander TOUS les appareils Tuya sans limite de quota cloud.
            Nécessite d'être sur le même réseau local que les appareils.
          </p>

          {/* Lien vers la page de saisie manuelle des local_keys */}
          <a
            href="/settings/tuya-local-keys"
            className="block mb-4 p-4 rounded-lg bg-amber-900/30 border border-amber-500/40 hover:bg-amber-900/50 transition-colors"
            data-testid="goto-tuya-local-keys-btn"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-amber-300 font-mono font-bold">⚙ SAISIE MANUELLE DES LOCAL_KEYS</div>
                <div className="text-xs text-amber-200/70 font-mono mt-1">
                  Contournement du quota Cloud Tuya. Saisir chaque local_key extraite de iot.tuya.com → Devices → Debug Device.
                </div>
              </div>
              <div className="text-amber-300 font-mono text-xl">→</div>
            </div>
          </a>

          {/* Stats */}
          {localDevices && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3 rounded-lg bg-black/40 border border-gray-700 text-center">
                <div className="text-2xl font-bold font-mono text-white">{localDevices.total}</div>
                <div className="text-xs font-mono text-gray-500">APPAREILS TOTAL</div>
              </div>
              <div className="p-3 rounded-lg bg-green-900/30 border border-green-500/30 text-center">
                <div className="text-2xl font-bold font-mono text-green-400">{localDevices.local_ready}</div>
                <div className="text-xs font-mono text-green-400/60">LOCAL PRÊT</div>
              </div>
              <div className="p-3 rounded-lg bg-orange-900/30 border border-orange-500/30 text-center">
                <div className="text-2xl font-bold font-mono text-orange-400">{localDevices.cloud_only}</div>
                <div className="text-xs font-mono text-orange-400/60">CLOUD SEUL</div>
              </div>
            </div>
          )}

          {/* Scan result */}
          {scanResult && (
            <div className={`p-3 rounded-lg mb-4 border ${
              scanResult.success
                ? 'bg-green-900/20 border-green-500/30 text-green-400'
                : 'bg-red-900/20 border-red-500/30 text-red-400'
            }`}>
              {scanResult.success ? (
                <div className="font-mono text-sm">
                  {scanResult.count} appareils trouvés sur le réseau local, {scanResult.cache_updated} IPs mises à jour.
                </div>
              ) : (
                <div className="font-mono text-sm">
                  Scan échoué: {scanResult.error}. Êtes-vous sur le même réseau que vos appareils Tuya ?
                </div>
              )}
            </div>
          )}

          {/* Device list */}
          {localDevices?.devices?.length > 0 && (
            <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-700">
              <table className="w-full text-sm font-mono">
                <thead className="bg-gray-900 sticky top-0">
                  <tr className="text-gray-400 text-xs">
                    <th className="p-2 text-left">APPAREIL</th>
                    <th className="p-2 text-left">CATÉGORIE</th>
                    <th className="p-2 text-left">IP LOCALE</th>
                    <th className="p-2 text-center">CLEF</th>
                    <th className="p-2 text-center">STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {localDevices.devices
                    .sort((a, b) => (b.local_ready ? 1 : 0) - (a.local_ready ? 1 : 0))
                    .map((d, i) => (
                    <tr key={d.device_id || i} className="border-t border-gray-800 hover:bg-gray-800/50">
                      <td className="p-2 text-gray-300 truncate max-w-[200px]" title={d.name}>{d.name}</td>
                      <td className="p-2 text-gray-500">{d.category || '--'}</td>
                      <td className="p-2 text-cyan-400">{d.ip || <span className="text-gray-600">Non détecté</span>}</td>
                      <td className="p-2 text-center">{d.has_local_key ? <span className="text-green-400">OK</span> : <span className="text-red-400">NON</span>}</td>
                      <td className="p-2 text-center">
                        {d.local_ready ? (
                          <span className="px-2 py-0.5 rounded text-[10px] bg-green-900/50 text-green-400 border border-green-500/30">LOCAL</span>
                        ) : d.online ? (
                          <span className="px-2 py-0.5 rounded text-[10px] bg-blue-900/50 text-blue-400 border border-blue-500/30">CLOUD</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-[10px] bg-gray-900/50 text-gray-500 border border-gray-600/30">HORS LIGNE</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Section Intégrations */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-xl p-6 text-white shadow-lg">
          <h2 className="text-2xl font-bold mb-2">Configuration des Intégrations</h2>
          <p className="text-purple-100">
            Ajoutez vos credentials pour connecter vos appareils et services
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {integrationTypes.map((integration) => {
            const configured = isConfigured(integration.type);
            const isEditing = editingType === integration.type;
            const status = saveStatus[integration.type];

            return (
              <div
                key={integration.type}
                className="bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-700"
                data-testid={`integration-card-${integration.type}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-xl font-semibold text-white">{integration.name}</h3>
                    {configured && (
                      <span className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-full">
                        ✓ Configuré
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {!isEditing ? (
                      <>
                        <button
                          onClick={() => handleEdit(integration.type)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          data-testid={`edit-btn-${integration.type}`}
                        >
                          {configured ? '✏️ Modifier' : '➕ Ajouter'}
                        </button>
                        {configured && (
                          <button
                            onClick={() => handleDelete(integration.type)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            data-testid={`delete-btn-${integration.type}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleSave(integration.type)}
                          disabled={status === 'saving'}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                          data-testid={`save-btn-${integration.type}`}
                        >
                          {status === 'saving' ? (
                            <span>Enregistrement...</span>
                          ) : status === 'success' ? (
                            <><Check className="w-4 h-4" /> <span>Enregistré !</span></>
                          ) : (
                            <><Save className="w-4 h-4" /> <span>Enregistrer</span></>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setEditingType(null);
                            setFormData({});
                          }}
                          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div className="space-y-4 mt-4">
                    {integration.fields.map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {field.label}
                        </label>
                        <input
                          type={field.type}
                          placeholder={field.placeholder || ''}
                          value={formData[field.key] || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, [field.key]: e.target.value })
                          }
                          className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          data-testid={`input-${integration.type}-${field.key}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="bg-blue-900 bg-opacity-50 border border-blue-700 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-blue-200 mb-2">💡 Aide</h4>
          <ul className="text-blue-100 space-y-2 text-sm">
            <li>• <strong>SmartLife/Tuya:</strong> Obtenez vos clés API sur IoT Platform de Tuya</li>
            <li>• <strong>Alexa:</strong> Créez une Skill sur le Amazon Developer Portal</li>
            <li>• <strong>ViCare:</strong> Utilisez vos identifiants ViCare et obtenez un Client ID depuis le portail développeur</li>
            <li>• <strong>Stufe a Pellet:</strong> Obtenez votre API Key depuis l'interface web de votre poêle</li>
            <li>• <strong>Growatt:</strong> Utilisez vos identifiants de l'application Growatt</li>
            <li>• Tous les credentials sont chiffrés en base de données 🔒</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;